import { ReactElement, useMemo } from 'react'
import { sortBy } from 'lodash-es'
import { MenuItem, TextField } from '@mui/material'
import { ConnectorSelection, PRIORITY_LIST_SELECTION } from '../../connectorSelection'

const PRIORITY_LIST_VALUE = '__priority-list__'
const UNCHANGED_VALUE = '__unchanged__'

interface SelectableConnection {
  guid: string
  short_name: string
  name: string
}

interface Props {
  /** `null` keeps each anchor's current connector; only offered with `offerUnchanged`. */
  value: ConnectorSelection | null
  onChange: (selection: ConnectorSelection | null) => void
  /** The project's Zug- or Schubverbinder. */
  connections: SelectableConnection[]
  /** The field label, e.g. "Zuganker" or "Schubanker". */
  label: string
  dataCy: string
  disabled?: boolean
  /** Adds "unverändert" (`null`), for the multi-edit panes. */
  offerUnchanged?: boolean
}

/** Lets the user fix the connector of a Zuganker or Schubanker, or leave it to the
 *  prioritization. */
const ConnectorSelectionSelect = ({
  value,
  onChange,
  connections,
  label,
  dataCy,
  disabled = false,
  offerUnchanged = false,
}: Props): ReactElement => {
  const sortedConnections = useMemo(
    () => sortBy(connections, connection => connection.short_name),
    [connections],
  )

  return (
    <TextField
      select
      size="small"
      label={label}
      value={
        value === null
          ? UNCHANGED_VALUE
          : value.policy === 'UserDefined'
          ? value.connectorId
          : PRIORITY_LIST_VALUE
      }
      onChange={event =>
        onChange(
          event.target.value === UNCHANGED_VALUE
            ? null
            : event.target.value === PRIORITY_LIST_VALUE
            ? PRIORITY_LIST_SELECTION
            : { policy: 'UserDefined', connectorId: event.target.value },
        )
      }
      disabled={disabled}
      fullWidth
      data-cy={dataCy}
    >
      {offerUnchanged && (
        <MenuItem value={UNCHANGED_VALUE}>
          <em>unverändert</em>
        </MenuItem>
      )}
      <MenuItem value={PRIORITY_LIST_VALUE}>Automatisch (Priorisierung)</MenuItem>
      {sortedConnections.map(connection => (
        <MenuItem key={connection.guid} value={connection.guid}>
          {connection.short_name} – {connection.name}
        </MenuItem>
      ))}
    </TextField>
  )
}

export default ConnectorSelectionSelect
